import React from 'react';
import { toast } from 'sonner';
import Button from '~/modules/components/global/button';

const DEFAULT_TOAST_OPTIONS = {
	duration: 4000,
	closeButton: true
};

function normalizeType(type) {
	if(type === 'warn') {
		return 'warning';
	}

	return type;
}

function buildOptions(options) {
	const allOptions = { ...DEFAULT_TOAST_OPTIONS, ...options };

	if(allOptions.autoClose !== undefined) {
		// react-toastify used `autoClose: false` to mean "never auto-dismiss";
		// Sonner expresses that as `duration: Infinity` (a falsy duration is invalid).
		allOptions.duration = allOptions.autoClose === false ? Infinity : allOptions.autoClose;
		delete allOptions.autoClose;
	}

	delete allOptions.theme;
	delete allOptions.pauseOnHover;
	delete allOptions.hideProgressBar;
	delete allOptions.closeOnClick;

	return allOptions;
}

const ToastService = {
	create(type, message, options) {
		const allOptions = buildOptions(options);
		const normalizedType = normalizeType(type);

		if(typeof toast[normalizedType] === 'function') {
			return toast[normalizedType](message, allOptions);
		}

		return toast(message, allOptions);
	},

	createWithAction(type, message, action, actionCb, options) {
		const allOptions = buildOptions(options);
		const normalizedType = normalizeType(type);
		const toastFn = typeof toast[normalizedType] === 'function' ? toast[normalizedType] : toast;

		return toastFn(message, {
			...allOptions,
			action: {
				label: action,
				onClick: actionCb
			}
		});
	},

	/**
	 * A toast carrying more than one choice.
	 *
	 * `closeOnClick` is off by default here: the handler it installs covers the
	 * whole toast, so with it on, reaching for one of these buttons dismisses the
	 * toast before the click lands on it. Each action closes the toast itself
	 * once it has run.
	 *
	 * @param {string} type react-toastify method — success, error, info, warning
	 * @param {string|element} message
	 * @param {array} actions [{ label, onClick, variant, testId }]
	 * @param {object} options react-toastify options
	 */
	createWithActions(type, message, actions = [], options) {
		const allOptions = {
			...DEFAULT_TOAST_OPTIONS,
			...options,
			// Sonner only applies its `[data-styled]` chrome (padding, width,
			// border-radius, font-size) to toasts it renders itself; a `jsx` toast
			// (which `toast.custom` always produces) is assumed to bring its own,
			// so without this it renders as an unpadded, content-sized box.
			// Recreate it here via the same CSS vars sonner's own toasts use.
			style: {
				width: 'var(--width)',
				padding: '1rem',
				borderRadius: 'var(--border-radius)',
				fontSize: '0.8125rem',
				...options?.style
			}
		};

		// A function rather than an element so we can hand it the dismisser for
		// this particular toast once sonner gives us the id (see toast.custom below).
		const content = ({ closeToast }) => (
			// A toast is a fixed 320px and clips what does not fit. Buttons carry
			// white-space: nowrap, so two of them side by side can be wider than
			// that — and a flex row that cannot wrap answers with its widest
			// possible line, stretching the content box. The message then wraps to
			// that phantom width and gets cut off mid-sentence along with the
			// button, which is what it looked like: text running under the
			// scrollbar. Wrapping keeps the row's demand down to its widest single
			// button; minWidth holds the rest to the toast.
			<div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
				{message}

				<div
					style={{
						display: 'flex',
						flexWrap: 'wrap',
						gap: 8,
						justifyContent: 'flex-end',
						marginTop: 12
					}}
				>
					{actions.map(({ label, onClick, variant = 'secondary', testId }) => (
						<Button
							key={label}
							variant={variant}
							size="sm"
							lighten
							data-testid={testId}
							onClick={() => {
								onClick?.();
								closeToast?.();
							}}
						>
							{label}
						</Button>
					))}
				</div>
			</div>
		);

		// sonner's typed shortcuts (toast.info, etc.) only accept a zero-arg
		// title function — no per-toast id, so no way to dismiss just this one.
		// toast.custom() is the primitive built for exactly this: it hands the
		// render function the toast's own id.
		return toast.custom((id) => content({ closeToast: () => toast.dismiss(id) }), allOptions);
	},

	dismiss(toastId) {
		toast.dismiss(toastId);
	}
};

export default ToastService;
